import { useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { FileText, ArrowLeft, Upload, Download, Type, PenLine, Merge, Trash2, Droplet } from 'lucide-react';
import { Button } from '../../components/ui/button';
import BrandLogo from '../../components/BrandLogo';
import { PDFDocument, StandardFonts, rgb, degrees } from 'pdf-lib';
import { toast } from '../../hooks/use-toast';
import { Toaster } from '../../components/ui/toaster';

const MODES = [
  { id: 'text', icon: Type, label: 'Add Text' },
  { id: 'sign', icon: PenLine, label: 'Signature' },
  { id: 'watermark', icon: Droplet, label: 'Watermark' },
  { id: 'merge', icon: Merge, label: 'Merge PDFs' },
];

export default function PdfEditorPage() {
  const navigate = useNavigate();
  const [pdfBytes, setPdfBytes] = useState(null);
  const [pdfName, setPdfName] = useState('');
  const [previewUrl, setPreviewUrl] = useState('');
  const [mode, setMode] = useState('text');
  const [textToAdd, setTextToAdd] = useState('CONFIDENTIAL');
  const [watermark, setWatermark] = useState('AROUND THE WORLD AI');
  const [signature, setSignature] = useState('');
  const [processing, setProcessing] = useState(false);
  const fileRef = useRef(null);
  const mergeRef = useRef(null);

  const onUpload = async (file) => {
    if (!file) return;
    if (file.type !== 'application/pdf') {
      toast({ title: 'PDF only', description: 'Please upload a .pdf file.' });
      return;
    }
    const buf = await file.arrayBuffer();
    setPdfBytes(new Uint8Array(buf));
    setPdfName(file.name);
    setPreviewUrl(URL.createObjectURL(file));
  };

  const download = (bytes, name) => {
    const blob = new Blob([bytes], { type: 'application/pdf' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = name;
    a.click();
    URL.revokeObjectURL(url);
  };

  const addText = async () => {
    if (!pdfBytes || !textToAdd.trim()) return;
    setProcessing(true);
    try {
      const doc = await PDFDocument.load(pdfBytes);
      const font = await doc.embedFont(StandardFonts.HelveticaBold);
      const pages = doc.getPages();
      const p = pages[0];
      const { width, height } = p.getSize();
      p.drawText(textToAdd, {
        x: 50,
        y: height - 80,
        size: 24,
        font,
        color: rgb(0.1, 0.55, 0.85),
      });
      const bytes = await doc.save();
      download(bytes, `text-added-${pdfName || 'document.pdf'}`);
      toast({ title: 'Text added ✅' });
    } catch (e) {
      toast({ title: 'Failed', description: e.message });
    } finally {
      setProcessing(false);
    }
  };

  const addWatermark = async () => {
    if (!pdfBytes || !watermark.trim()) return;
    setProcessing(true);
    try {
      const doc = await PDFDocument.load(pdfBytes);
      const font = await doc.embedFont(StandardFonts.HelveticaBold);
      const pages = doc.getPages();
      pages.forEach((p) => {
        const { width, height } = p.getSize();
        p.drawText(watermark, {
          x: width / 2 - watermark.length * 8,
          y: height / 2,
          size: 40,
          font,
          color: rgb(0.75, 0.75, 0.75),
          opacity: 0.35,
          rotate: degrees(-45),
        });
      });
      const bytes = await doc.save();
      download(bytes, `watermarked-${pdfName || 'document.pdf'}`);
      toast({ title: 'Watermark applied ✅' });
    } catch (e) {
      toast({ title: 'Failed', description: e.message });
    } finally {
      setProcessing(false);
    }
  };

  const addSignature = async () => {
    if (!pdfBytes || !signature.trim()) return;
    setProcessing(true);
    try {
      const doc = await PDFDocument.load(pdfBytes);
      const font = await doc.embedFont(StandardFonts.TimesRomanBoldItalic);
      const pages = doc.getPages();
      const p = pages[pages.length - 1];
      p.drawText(signature, {
        x: 60,
        y: 80,
        size: 28,
        font,
        color: rgb(0.05, 0.1, 0.7),
      });
      p.drawLine({
        start: { x: 60, y: 70 },
        end: { x: 60 + signature.length * 15, y: 70 },
        thickness: 1.2,
        color: rgb(0.05, 0.1, 0.7),
      });
      p.drawText(`Signed: ${new Date().toLocaleDateString()}`, {
        x: 60,
        y: 55,
        size: 10,
        color: rgb(0.4, 0.4, 0.4),
      });
      const bytes = await doc.save();
      download(bytes, `signed-${pdfName || 'document.pdf'}`);
      toast({ title: 'Signature added ✅' });
    } catch (e) {
      toast({ title: 'Failed', description: e.message });
    } finally {
      setProcessing(false);
    }
  };

  const mergeWith = async (extra) => {
    if (!pdfBytes) return toast({ title: 'Upload the first PDF first' });
    if (!extra) return;
    setProcessing(true);
    try {
      const first = await PDFDocument.load(pdfBytes);
      const buf = await extra.arrayBuffer();
      const second = await PDFDocument.load(buf);
      const copied = await first.copyPages(second, second.getPageIndices());
      copied.forEach((p) => first.addPage(p));
      const bytes = await first.save();
      download(bytes, `merged.pdf`);
      toast({ title: 'PDFs merged ✅' });
    } catch (e) {
      toast({ title: 'Failed', description: e.message });
    } finally {
      setProcessing(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#08061a] text-white">
      <div className="sticky top-0 z-50 border-b border-white/10 bg-slate-950/80 backdrop-blur-xl">
        <div className="max-w-7xl mx-auto px-6 h-14 flex items-center gap-4">
          <button
            data-testid="pdf-back"
            onClick={() => navigate('/')}
            className="text-white/70 hover:text-white flex items-center gap-2 text-sm"
          >
            <ArrowLeft size={16} /> Home
          </button>
          <span className="text-white/25">/</span>
          <span className="text-sm text-white/80">Templates</span>
          <span className="text-white/25">/</span>
          <span className="text-sm text-fuchsia-300 font-semibold">PDF Editor</span>
          <div className="ml-auto">
            <BrandLogo size={24} tone="light" />
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-6 py-10 grid lg:grid-cols-[380px_1fr] gap-8">
        {/* LEFT panel */}
        <div className="space-y-5">
          <div>
            <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-white/5 border border-white/15 mb-4">
              <FileText size={13} className="text-fuchsia-300" />
              <span className="text-[11px] tracking-widest uppercase font-bold text-white/85">
                PDF Editor
              </span>
            </div>
            <h1 className="text-3xl font-black tracking-tight">Edit PDFs in your browser</h1>
            <p className="text-sm text-white/60 mt-1.5">
              Text, signatures, watermarks and merging. Nothing leaves your device.
            </p>
          </div>

          {/* Upload */}
          <div>
            <input
              ref={fileRef}
              type="file"
              accept="application/pdf"
              onChange={(e) => onUpload(e.target.files?.[0])}
              className="hidden"
              data-testid="pdf-upload-input"
            />
            <button
              onClick={() => fileRef.current?.click()}
              className="w-full rounded-xl border-2 border-dashed border-white/15 hover:border-fuchsia-400/50 hover:bg-white/5 p-6 text-center transition"
              data-testid="pdf-upload-btn"
            >
              <Upload size={22} className="mx-auto text-white/60 mb-2" />
              <div className="font-bold text-sm">{pdfName || 'Upload a PDF'}</div>
              <div className="text-xs text-white/50 mt-1">
                {pdfName ? 'Click to change' : 'Drag or click to select'}
              </div>
            </button>
          </div>

          {pdfBytes && (
            <>
              {/* Mode tabs */}
              <div className="grid grid-cols-2 gap-2">
                {MODES.map((m) => {
                  const Icon = m.icon;
                  return (
                    <button
                      key={m.id}
                      data-testid={`pdf-mode-${m.id}`}
                      onClick={() => setMode(m.id)}
                      className={`h-11 rounded-xl text-[11px] font-bold uppercase tracking-widest transition flex items-center justify-center gap-1.5 ${
                        mode === m.id
                          ? 'bg-gradient-to-r from-fuchsia-500 to-purple-600 text-white shadow-lg'
                          : 'bg-white/5 border border-white/15 text-white/70 hover:bg-white/10'
                      }`}
                    >
                      <Icon size={13} />
                      {m.label}
                    </button>
                  );
                })}
              </div>

              {mode === 'text' && (
                <div className="space-y-3">
                  <input
                    data-testid="pdf-text-input"
                    value={textToAdd}
                    onChange={(e) => setTextToAdd(e.target.value)}
                    placeholder="Text to add"
                    className="w-full h-11 px-3 rounded-xl bg-white/5 border border-white/15 focus:border-fuchsia-400 outline-none text-sm"
                  />
                  <Button
                    data-testid="pdf-add-text"
                    onClick={addText}
                    disabled={processing}
                    className="w-full h-11 rounded-xl bg-gradient-to-r from-fuchsia-500 to-purple-600 text-white font-bold"
                  >
                    <Download size={14} className="mr-2" />
                    Add text & download
                  </Button>
                </div>
              )}

              {mode === 'watermark' && (
                <div className="space-y-3">
                  <input
                    data-testid="pdf-watermark-input"
                    value={watermark}
                    onChange={(e) => setWatermark(e.target.value)}
                    placeholder="Watermark text"
                    className="w-full h-11 px-3 rounded-xl bg-white/5 border border-white/15 focus:border-fuchsia-400 outline-none text-sm"
                  />
                  <Button
                    data-testid="pdf-add-watermark"
                    onClick={addWatermark}
                    disabled={processing}
                    className="w-full h-11 rounded-xl bg-gradient-to-r from-fuchsia-500 to-purple-600 text-white font-bold"
                  >
                    <Droplet size={14} className="mr-2" />
                    Apply watermark
                  </Button>
                </div>
              )}

              {mode === 'sign' && (
                <div className="space-y-3">
                  <input
                    data-testid="pdf-sign-input"
                    value={signature}
                    onChange={(e) => setSignature(e.target.value)}
                    placeholder="Your name"
                    className="w-full h-11 px-3 rounded-xl bg-white/5 border border-white/15 focus:border-fuchsia-400 outline-none text-sm"
                  />
                  <Button
                    data-testid="pdf-add-signature"
                    onClick={addSignature}
                    disabled={processing}
                    className="w-full h-11 rounded-xl bg-gradient-to-r from-fuchsia-500 to-purple-600 text-white font-bold"
                  >
                    <PenLine size={14} className="mr-2" />
                    Add signature
                  </Button>
                </div>
              )}

              {mode === 'merge' && (
                <div className="space-y-3">
                  <input
                    ref={mergeRef}
                    type="file"
                    accept="application/pdf"
                    onChange={(e) => mergeWith(e.target.files?.[0])}
                    className="hidden"
                    data-testid="pdf-merge-input"
                  />
                  <p className="text-xs text-white/60 leading-relaxed">
                    Upload a second PDF &mdash; we&rsquo;ll append it to the first and give you the merged file.
                  </p>
                  <Button
                    data-testid="pdf-merge-btn"
                    onClick={() => mergeRef.current?.click()}
                    disabled={processing}
                    className="w-full h-11 rounded-xl bg-gradient-to-r from-fuchsia-500 to-purple-600 text-white font-bold"
                  >
                    <Merge size={14} className="mr-2" />
                    Pick 2nd PDF & merge
                  </Button>
                </div>
              )}

              <button
                data-testid="pdf-clear"
                onClick={() => {
                  setPdfBytes(null);
                  setPdfName('');
                  if (previewUrl) URL.revokeObjectURL(previewUrl);
                  setPreviewUrl('');
                }}
                className="w-full text-xs text-rose-300 hover:text-rose-200 flex items-center justify-center gap-1.5"
              >
                <Trash2 size={12} /> Remove PDF
              </button>
            </>
          )}
        </div>

        {/* RIGHT: Preview */}
        <div>
          {!previewUrl && (
            <div className="border-2 border-dashed border-white/10 rounded-2xl h-[600px] flex items-center justify-center text-white/50">
              <div className="text-center">
                <FileText size={40} className="mx-auto text-white/25 mb-4" />
                <div className="font-bold text-white/80">Upload a PDF to start editing</div>
                <div className="text-sm mt-2">All processing happens in your browser.</div>
              </div>
            </div>
          )}
          {previewUrl && (
            <div className="rounded-2xl overflow-hidden border border-white/10 h-[80vh] bg-white/5">
              <iframe
                src={previewUrl}
                title="PDF preview"
                className="w-full h-full"
                data-testid="pdf-preview-iframe"
              />
            </div>
          )}
        </div>
      </div>
      <Toaster />
    </div>
  );
}
